import React, { useState } from 'react';
import { Mail, MessageCircle, Send, CheckCircle, Loader } from 'lucide-react';
import { contactService } from '../services/api-service';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await contactService.sendContactMessage(formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error sending contact message:', error);
      setError('Failed to send message. Please try again or contact us directly at info@backtopeople.com');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="contact-page">
        <section className="contact-success section">
          <div className="container">
            <div className="contact-success-content">
              <CheckCircle size={80} className="contact-success-icon" />
              <h1>Message Sent!</h1>
              <p>
                Thank you for reaching out, {formData.name}! We'll get back to you 
                at <strong>{formData.email}</strong> as soon as possible.
              </p>
              <a href="/" className="btn">
                Back to Home
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {/* Header */}
      <section className="contact-header">
        <div className="container">
          <div className="contact-header-content">
            <MessageCircle size={48} className="contact-header-icon" />
            <h1>Get in Touch</h1>
            <p>
              Have questions? Want to perform at our events? Interested in venue partnerships? 
              We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="contact-content section">
        <div className="container">
          <div className="contact-grid">
            {/* Form */}
            <div className="contact-form-container">
              <h2>Send us a Message</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="contact-form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="artist">Artist Application</option>
                    <option value="venue">Venue Partnership</option>
                    <option value="event">Event Question</option>
                    <option value="collaboration">Collaboration Opportunity</option>
                    <option value="feedback">Feedback</option>
                  </select>
                </div>

                <div className="contact-form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us more..."
                  />
                </div>

                {error && (
                  <div className="contact-error" style={{ color: '#ff4444', marginBottom: '1rem', padding: '0.75rem', background: '#ffe6e6', borderRadius: '4px' }}>
                    {error}
                  </div>
                )}

                <button type="submit" className="btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader size={20} className="spinning" style={{ animation: 'spin 1s linear infinite' }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info">
              <h2>Connect With Us</h2>
              
              <div className="contact-info-section">
                <h3>Email</h3>
                <p>
                  <Mail size={20} />
                  <a href="mailto:info@backtopeople.com">info@backtopeople.com</a>
                </p>
              </div>

              <div className="contact-info-section">
                <h3>Social Media</h3>
                <p>Follow us on Instagram for updates, event photos, and community vibes</p>
                <a href="https://instagram.com/backtopeople" target="_blank" rel="noopener noreferrer" className="contact-social-link">@backtopeople</a>
              </div>

              <div className="contact-info-section">
                <h3>Location</h3>
                <p>Queensland, Australia</p>
                <p className="contact-info-note">
                  Event locations are shared upon RSVP confirmation to maintain 
                  venue privacy and respect.
                </p>
              </div>

              <div className="contact-info-section">
                <h3>For Venues</h3>
                <p>
                  Interested in hosting a B2P event? Check out our{' '}
                  <a href="/venue-portfolio">Venue Portfolio</a> to see what we offer.
                </p>
              </div>

              <div className="contact-info-section">
                <h3>For Artists</h3>
                <p>
                  Want to perform at our events? We're always looking for talented 
                  artists who align with our values of community, respect, and 
                  authentic connection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Reminder */}
      <section className="contact-philosophy section bg-black text-white">
        <div className="container">
          <blockquote className="contact-quote">
            "If we can't respect the earth, how can we respect each other?"
          </blockquote>
          <p className="contact-philosophy-text">
            At Back to People, every interaction is built on mutual respect, authenticity, 
            and a commitment to creating meaningful connections.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;

